import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sun, Moon, Languages } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import DevUserSwitcher from "@/components/DevUserSwitcher";
import { WS_TOKENS } from "@/lib/workspaceTheme";

/**
 * Slim 56px top bar for the workspace. Contains:
 *   - Screen title (dynamic from location)
 *   - Notifications bell
 *   - User avatar (initial letter, links to /my-earnings profile-ish)
 *
 * Deliberately minimal: no role text, no logout, no theme toggle — those
 * live in the sidebar / account panel.
 */
const TITLES = {
  "/": { ar: "الرئيسية", en: "Home" },
  "/applications": { ar: "المعاملات", en: "Applications" },
  "/applications/new": { ar: "معاملة جديدة", en: "New Application" },
  "/admin/users": { ar: "المستخدمون والصلاحيات", en: "Users & Access" },
  "/my-earnings": { ar: "حسابي", en: "My Account" },
  "/attendance": { ar: "الحضور والانصراف", en: "Attendance" },
  "/finance": { ar: "المحاسبة", en: "Accounting" },
  "/admin/branding": { ar: "الهوية", en: "Branding" },
  "/admin/pricing-catalog": { ar: "كتالوج التسعير", en: "Pricing Catalog" },
  "/admin/promo-codes": { ar: "أكواد الخصم والتسويق", en: "Promo Codes" },
  "/admin/bonus-policy": { ar: "سياسة البونص", en: "Bonus Policy" },
  "/admin/system-health": { ar: "صحة النظام", en: "System Health" },
  "/admin/banks": { ar: "البنوك", en: "Banks" },
  "/admin/receipt-template": { ar: "قالب الإيصال", en: "Receipt Template" },
  "/admin/performance": { ar: "التقييم", en: "Performance" },
  "/admin/document-requirements": { ar: "متطلبات المستندات", en: "Doc Requirements" },
  "/admin/attendance": { ar: "الحضور — الإدارة", en: "Attendance Admin" },
  "/admin/portal-campaigns": { ar: "إعلانات البوابة", en: "Portal Campaigns" },
  "/admin/invoice-verify": { ar: "التحقق من الفواتير", en: "Invoice Verifier" },
  "/admin/muaqqibs": { ar: "دليل المعقّبين", en: "Muaqqib Directory" },
  "/admin/countries": { ar: "الدول", en: "Countries" },
  "/admin/settings": { ar: "الإعدادات", en: "Settings" },
};

function resolveTitle(pathname, search, lang) {
  if (pathname.startsWith("/applications/") && pathname !== "/applications/new") {
    return lang === "ar" ? "تفاصيل المعاملة" : "Application";
  }
  // Focused views on the Applications list (Invoices / Follow-up / Archive)
  if (pathname === "/applications") {
    const params = new URLSearchParams(search || "");
    if (params.get("archived") === "1") return lang === "ar" ? "الأرشيف" : "Archive";
    const view = params.get("view");
    if (view === "invoices") return lang === "ar" ? "الفواتير" : "Invoices";
    if (view === "follow_up") return lang === "ar" ? "المتابعة" : "Follow-up";
  }
  const key = TITLES[pathname];
  if (key) return key[lang] || key.ar;
  return "";
}

export default function WorkspaceTopBar({ showBack = false }) {
  const { pathname, search } = useLocation();
  const { theme, lang, dir, themeName, toggleTheme, setLang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const title = resolveTitle(pathname, search, lang);
  const initial = (user?.full_name || user?.username || "?").trim().charAt(0).toUpperCase();
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  const css = {
    bar: {
      position: "sticky",
      top: 0,
      zIndex: 90,
      height: WS_TOKENS.topBarHeight,
      background: theme.headerBg,
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderBottom: `1px solid ${theme.borderSoft}`,
      display: "flex", alignItems: "center", gap: 8,
      paddingInline: 16,
      paddingTop: "env(safe-area-inset-top, 0px)",
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
    },
    backBtn: {
      width: 40, height: 40, borderRadius: 10,
      background: "transparent", border: "none",
      color: theme.text, cursor: "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    },
    title: {
      flex: 1, minWidth: 0,
      color: theme.text, fontSize: 16, fontWeight: 800,
      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    },
    right: { display: "flex", alignItems: "center", gap: 6 },
    iconBtn: {
      width: 36, height: 36, borderRadius: 10,
      background: "transparent",
      border: `1px solid ${theme.borderSoft}`,
      color: theme.text, cursor: "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "background 160ms",
      fontFamily: "inherit",
    },
    avatar: {
      width: 34, height: 34, borderRadius: "50%",
      background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`,
      color: "#0A0F19", fontWeight: 900, fontSize: 13,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", border: "none",
    },
  };

  return (
    <div style={css.bar} data-testid="workspace-topbar">
      {showBack && (
        <button
          onClick={() => navigate(-1)}
          style={css.backBtn}
          data-testid="topbar-back"
          aria-label={lang === "ar" ? "رجوع" : "Back"}
        >
          <BackIcon size={20} strokeWidth={1.75} />
        </button>
      )}
      <div style={css.title} data-testid="topbar-title">{title}</div>
      <div style={css.right}>
        <DevUserSwitcher />
        <NotificationsBell />
        <button
          type="button"
          onClick={() => setLang(lang === "ar" ? "en" : "ar")}
          style={css.iconBtn}
          data-testid="topbar-lang-toggle"
          title={lang === "ar" ? "English" : "العربية"}
          aria-label={lang === "ar" ? "English" : "العربية"}
        >
          <Languages size={17} strokeWidth={1.9} />
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          style={css.iconBtn}
          data-testid="topbar-theme-toggle"
          title={themeName === "dark" ? (lang === "ar" ? "الوضع الفاتح" : "Light mode") : (lang === "ar" ? "الوضع الداكن" : "Dark mode")}
          aria-label="theme"
        >
          {themeName === "dark" ? <Sun size={17} strokeWidth={1.9} /> : <Moon size={17} strokeWidth={1.9} />}
        </button>
        <button
          type="button"
          onClick={() => navigate("/my-earnings")}
          style={css.avatar}
          data-testid="topbar-avatar"
          aria-label={lang === "ar" ? "حسابي" : "Account"}
          title={user?.full_name}
        >
          {initial}
        </button>
      </div>
    </div>
  );
}
